import { IconClipboardList } from '@tabler/icons-react';
import type { ColumnDef } from '@tanstack/react-table';
import { ChevronRight } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { DataTable } from 'components/DataTable';
import { DynamicDeliveryTag } from 'components/DynamicDeliveryTag';
import { EllipsisTooltip } from 'components/EllipsisTooltip';
import { FreefoodIcon } from 'components/FreefoodIcon';
import { Loader, LoaderModeType } from 'components/Loader';
import { DeliveryDestinationType, useGetTourQuery } from 'data/__generated__';
import { formatDurationMinutes } from 'helpers/dateTimes';
import { BUNDLE_TYPES_LABELS } from 'pages/Bundles/constants/types';
import { DELIVERY_STATES_LABELS } from 'pages/Deliveries/constants/states';
import { UNASSIGNED_COLOR } from 'pages/DynamicDeliveryPlanning/constants/approColors';
import { MICROSTORE_COLUMN_TEMPLATE_TYPE_LABELS } from 'pages/MicrostoreColumnTemplates/constants/types';

import { TableCell } from '@/components/ui/table';

import {
    DESTINATION_BY_DELIVERY_TYPE,
    TASK_STATE_LABELS,
    STATUT_COLOR,
    UNASSIGNED_STATUT_COLOR,
    boxesByFlowLabel,
    deliveryColumnCounts,
    deliveryColumnTypes,
    deliveryHref,
    formatWindow,
    getStopState,
    hasFreefood,
    isTask,
    orderedStops,
    stopCountLabel,
    stopLabel,
    stopWindow,
    tourColumnCount,
    tourHref,
    tourOrigin,
    tourStateLabel,
    tourStateOptions,
    type Tour,
    type TourDelivery,
    type TourStopItem,
    type TourTask,
} from '../helpers';
import { DeliveryDetails } from '../TourDetails/DeliveryCard';
import { TaskDetails } from '../TourDetails/TaskCard';
import { dueScanSteps, type ScanStep } from '../TourDetails/helpers';
import { OriginIcon } from './OriginIcon';
import { STATE_TONE, StatePill, TASK_STATE_TONE } from './StopStatus';
import { TaskIcon } from './TaskStop';

/** One stop of the day, delivery or task; `tour` is null for the « à affecter » pool. */
type TourListRow = { stop: TourStopItem; tour: Tour | null; seq: number | null };

/** The delivery of the row, null on a task row — which has no columns nor boxes. */
const deliveryOf = ({ stop }: TourListRow): TourDelivery | null => (isTask(stop) ? null : stop);

/**
 * Boxes of the stop through one scan step, « 3/4 ». Only a gap is coloured: red once the step is due
 * (`dueScanSteps`, shared with the tour page's issues) and boxes are missing, muted while it is not due yet.
 */
const scanColumn = (step: ScanStep, header: string): ColumnDef<TourListRow, unknown> => ({
    id: step,
    accessorFn: (row) => deliveryOf(row)?.boxes.filter((box) => box[step] != null).length ?? 0,
    header,
    enableGlobalFilter: false,
    cell: ({ row, getValue }) => {
        const delivery = deliveryOf(row.original);
        const total = delivery?.boxes.length ?? 0;
        if (!delivery || !total) return '—';
        const done = getValue<number>();
        const status =
            done === total ? 'done' : dueScanSteps(row.original.tour, delivery)[step] ? 'missing' : 'pending';
        return (
            <ScanCount $status={status}>
                {done}/{total}
            </ScanCount>
        );
    },
});

/** A count of the stop — « — » for none, so the few that matter stand out in a dense table. */
const countColumn = (
    id: string,
    header: string,
    count: (delivery: TourDelivery) => number,
): ColumnDef<TourListRow, unknown> => ({
    id,
    accessorFn: (row) => {
        const delivery = deliveryOf(row);
        return delivery ? count(delivery) : 0;
    },
    header,
    enableGlobalFilter: false,
    cell: ({ getValue }) => getValue<number>() || '—',
});

const toOptions = (labels: Record<string, string>) =>
    Object.entries(labels).map(([value, label]) => ({ value, label }));

/**
 * A toolbar filter, same choices as the timeline's. Hidden column: `enableHiding: false` keeps it out of the
 * visibility menu AND stops DataTable from revealing it once the filter is set. A stop matches when ANY of its
 * values is picked — it serves several column types, carries several flux.
 */
const filterColumn = (
    id: string,
    header: string,
    options: { value: string; label: string }[],
    valuesOf: (row: TourListRow) => string[],
): ColumnDef<TourListRow, unknown> => ({
    id,
    accessorFn: valuesOf,
    header,
    enableHiding: false,
    enableSorting: false,
    enableGlobalFilter: false,
    meta: { defaultVisible: false, filter: { type: 'multi-select', options } },
    filterFn: (row, columnId, picked: string[]) =>
        !picked?.length || row.getValue<string[]>(columnId).some((value) => picked.includes(value)),
});

const COLUMNS: ColumnDef<TourListRow, unknown>[] = [
    // Hidden column used only for grouping (kept out of the column-visibility menu and of the search: it holds ids).
    {
        id: 'tour',
        accessorFn: (row) => row.tour?._id ?? 'a-affecter',
        header: 'Tournée',
        enableHiding: false,
        enableGlobalFilter: false,
        meta: { defaultVisible: false },
    },
    // Hidden columns used only for the search (driver, organisation): none of them is displayed as a column.
    {
        id: 'deliveryPerson',
        accessorFn: (row) => row.tour?.deliveryPerson?.fullname ?? '',
        header: 'Livreur',
        enableHiding: false,
        meta: { defaultVisible: false },
    },
    {
        id: 'organization',
        accessorFn: (row) => deliveryOf(row)?.organizationName ?? '',
        header: 'Organisation',
        enableHiding: false,
        meta: { defaultVisible: false },
    },
    filterColumn('tourState', 'Statut', tourStateOptions(), (row) => (row.tour ? [row.tour.state] : [])),
    filterColumn('columnType', 'Colonnes', toOptions(MICROSTORE_COLUMN_TEMPLATE_TYPE_LABELS), (row) => {
        const delivery = deliveryOf(row);
        return delivery ? deliveryColumnTypes(delivery) : [];
    }),
    filterColumn(
        'flow',
        'Flux',
        toOptions(BUNDLE_TYPES_LABELS),
        (row) => deliveryOf(row)?.boxes.map((box) => box.type) ?? [],
    ),
    filterColumn(
        'deliveryType',
        'Type de livraison',
        toOptions({ [DeliveryDestinationType.Store]: 'Store', [DeliveryDestinationType.Freefood]: 'Freefood' }),
        (row) => {
            const delivery = deliveryOf(row);
            return delivery ? [DESTINATION_BY_DELIVERY_TYPE[delivery.type] ?? ''] : [];
        },
    ),
    {
        id: 'seq',
        accessorFn: (row) => row.seq,
        header: '#',
        enableGlobalFilter: false,
        cell: ({ row }) => {
            const { stop, seq } = row.original;
            return (
                <div className="flex items-center gap-2">
                    {seq ?? '—'}
                    {isTask(stop) ? (
                        <TaskIcon />
                    ) : (
                        <>
                            <DynamicDeliveryTag isDynamic={stop.isDynamic} label="Livraison dynamique" />
                            {hasFreefood(stop) && <FreefoodIcon size={14} />}
                        </>
                    )}
                </div>
            );
        },
    },
    {
        id: 'label',
        accessorFn: (row) => stopLabel(row.stop),
        header: 'Arrêt',
        cell: ({ getValue }) => (
            <EllipsisTooltip text={getValue<string>()} className="max-w-[220px] font-semibold text-foreground" />
        ),
    },
    {
        id: 'state',
        accessorFn: ({ stop }) =>
            isTask(stop) ? TASK_STATE_LABELS[stop.state] : DELIVERY_STATES_LABELS[getStopState(stop)],
        header: 'État',
        enableGlobalFilter: false,
        cell: ({ row, getValue }) => {
            const { stop } = row.original;
            const tone = isTask(stop) ? TASK_STATE_TONE[stop.state] : (STATE_TONE[getStopState(stop)] ?? 'neutral');
            return <StatePill $tone={tone}>{getValue<string>()}</StatePill>;
        },
    },
    {
        id: 'window',
        accessorFn: (row) => formatWindow(stopWindow(row.stop).start, stopWindow(row.stop).stop),
        header: 'Créneau',
        enableGlobalFilter: false,
    },
    {
        id: 'serviceTime',
        accessorFn: (row) => row.stop.theoricServiceTime,
        header: 'Sur place',
        enableGlobalFilter: false,
        cell: ({ getValue }) => {
            const minutes = getValue<number | null>();
            return minutes != null ? formatDurationMinutes(minutes) : '—';
        },
    },
    {
        id: 'columns',
        accessorFn: (row) => deliveryOf(row)?.deliveredMicrostoreColumnIds.length ?? null,
        header: 'Colonnes',
        enableGlobalFilter: false,
        cell: ({ getValue }) => getValue<number | null>() ?? '—',
    },
    countColumn('columnsDry', 'Col. sec', (delivery) => deliveryColumnCounts(delivery).others),
    countColumn('columnsFresh', 'Col. frais', (delivery) => deliveryColumnCounts(delivery).fresh),
    countColumn('columnsHotDrinks', 'Col. boissons chaudes', (delivery) => deliveryColumnCounts(delivery).hotDrinks),
    scanColumn('departureScannedAt', 'Contrôle départ'),
    scanColumn('loadedAt', 'Chargement'),
    scanColumn('setupScannedAt', 'Rangement'),
    scanColumn('returnedAt', 'Retour'),
];

/**
 * The day as a list: one row per stop, grouped by tour in the timeline's order, « à affecter » last. Read-only —
 * the dispatch actions stay on the timeline and the map.
 */
export const TourList = ({
    tours,
    unassignedDeliveries,
    unassignedTasks,
}: {
    tours: Tour[];
    unassignedDeliveries: TourDelivery[];
    unassignedTasks: TourTask[];
}) => {
    const rows = useMemo<TourListRow[]>(
        () => [
            ...tours.flatMap((tour) => orderedStops(tour).map((stop, index) => ({ stop, tour, seq: index + 1 }))),
            ...unassignedDeliveries.map((stop) => ({ stop, tour: null, seq: null })),
            ...unassignedTasks.map((stop) => ({ stop, tour: null, seq: null })),
        ],
        [tours, unassignedDeliveries, unassignedTasks],
    );

    // Stops unfolded to show their details. A delivery unfolds only in a tour (its boxes come with the tour); a task
    // carries its whole report, so it unfolds anywhere.
    const [openIds, setOpenIds] = useState<Set<string>>(new Set());
    const toggle = useCallback(({ stop, tour }: TourListRow) => {
        if (!tour && !isTask(stop)) return;
        setOpenIds((current) => {
            const next = new Set(current);
            if (!next.delete(stop._id)) next.add(stop._id);
            return next;
        });
    }, []);

    const columns = useMemo<ColumnDef<TourListRow, unknown>[]>(
        () => [
            {
                id: 'expand',
                header: '',
                enableSorting: false,
                enableHiding: false,
                enableGlobalFilter: false,
                cell: ({ row }) =>
                    (row.original.tour || isTask(row.original.stop)) && (
                        <Chevron $open={openIds.has(row.original.stop._id)}>
                            <ChevronRight size={16} />
                        </Chevron>
                    ),
            },
            ...COLUMNS,
        ],
        [openIds],
    );

    return (
        <TableContainer>
            <DataTable<TourListRow>
                columns={columns}
                data={rows}
                filterMode="client"
                getRowId={(row) => row.stop._id}
                getRowLink={({ stop, tour }) =>
                    isTask(stop) ? (tour ? tourHref(tour._id) : null) : deliveryHref(stop._id)
                }
                onRowClick={toggle}
                renderRowDetails={({ stop, tour }) => {
                    if (!openIds.has(stop._id)) return null;
                    if (isTask(stop)) return <TaskDetails task={stop} />;
                    return tour ? <StopDetails tourId={tour._id} deliveryId={stop._id} /> : null;
                }}
                groupBy={['tour']}
                renderGroupRow={({ row, leafRows, columnCount }) => {
                    const tour = leafRows[0]?.original.tour ?? null;
                    return (
                        <GroupCell colSpan={columnCount} onClick={() => row.toggleExpanded()}>
                            <GroupHead>
                                <Chevron $open={row.getIsExpanded()}>
                                    <ChevronRight size={16} />
                                </Chevron>
                                {tour ? (
                                    <TourHeader tour={tour} />
                                ) : (
                                    <>
                                        <TourBadge $color={UNASSIGNED_STATUT_COLOR}>À affecter</TourBadge>
                                        <Meta>
                                            {stopCountLabel(unassignedDeliveries.length, unassignedTasks.length)} sans
                                            tournée
                                        </Meta>
                                    </>
                                )}
                            </GroupHead>
                        </GroupCell>
                    );
                }}
                searchPlaceholder="Rechercher une organisation, un site, un livreur…"
                emptyMessage="Aucun arrêt pour cette date."
                disableUrlSync
            />
        </TableContainer>
    );
};

/**
 * An unfolded stop: the tour page's delivery details (boxes with QR code and scans, arrival and closure). Fetched with
 * its tour on first open — the day query stays light, and the stops of one tour share a single request.
 */
const StopDetails = ({ tourId, deliveryId }: { tourId: string; deliveryId: string }) => {
    const { data, error } = useGetTourQuery({ variables: { tourId }, fetchPolicy: 'cache-and-network' });
    const delivery = data?.tour.deliveries.find((candidate) => candidate._id === deliveryId);

    if (delivery) return <DeliveryDetails delivery={delivery} />;
    if (data) return <Meta>Cette livraison n’est plus dans la tournée — rechargez la page.</Meta>;
    if (error) return <Meta>Détails indisponibles : {error.message}</Meta>;
    return <Loader size="20px" mode={LoaderModeType.Spin} />;
};

/** The timeline's lane head (name, origin, statut, appro) — counted on the whole tour, not on the searched rows. */
const TourHeader = ({ tour }: { tour: Tour }) => {
    const { search } = useLocation();
    const statut = tourStateLabel(tour.state);
    const columnCount = tourColumnCount(tour);
    const boxesByFlow = boxesByFlowLabel(tour);
    const taskCount = tour.stops.filter(({ __typename }) => __typename === 'Task').length;

    return (
        <>
            <TourBadge $color={tour.color || UNASSIGNED_COLOR}>{tour.name}</TourBadge>
            <OriginIcon origin={tourOrigin(tour)} />
            <StatutPill $color={STATUT_COLOR[statut]}>{statut}</StatutPill>
            {tour.deliveryPerson?.fullname && <Appro>{tour.deliveryPerson.fullname}</Appro>}
            <Meta>
                {stopCountLabel(tour.deliveries.length, taskCount)} · {columnCount} colonne
                {columnCount > 1 ? 's' : ''}
                {boxesByFlow && ` · Caisses : ${boxesByFlow}`}
            </Meta>
            <DetailsLink
                to={{ pathname: tourHref(tour._id), search }}
                title="Détails de la tournée"
                onClick={(event) => event.stopPropagation()}
            >
                <IconClipboardList size={22} />
            </DetailsLink>
        </>
    );
};

const TableContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
`;

const GroupCell = styled(TableCell)`
    padding: 0;
    cursor: pointer;
    user-select: none;
    background: ${({ theme }) => theme.cardBackgroundColor};
    border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const GroupHead = styled.div`
    position: sticky;
    left: 0;
    width: 100cqw;
    padding: 8px;
    display: flex;
    align-items: center;
    gap: 10px;
`;

const Chevron = styled.span<{ $open: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.infoTextColor};
    transition: transform 0.18s ease;
    transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
    flex-shrink: 0;
`;

const TourBadge = styled.span<{ $color: string }>`
    height: 22px;
    line-height: 22px;
    padding: 0 9px;
    border-radius: 6px;
    color: #fff;
    font-weight: 800;
    font-size: 11px;
    background: ${({ $color }) => $color};
`;

const StatutPill = styled.span<{ $color: string }>`
    font-size: 11px;
    font-weight: 700;
    padding: 3px 9px;
    border-radius: 999px;
    color: ${({ $color }) => $color};
    background: ${({ $color }) => `color-mix(in srgb, ${$color} 13%, transparent)`};
`;

const Appro = styled.span`
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
`;

const ScanCount = styled.span<{ $status: 'done' | 'missing' | 'pending' }>`
    font-weight: ${({ $status }) => ($status === 'missing' ? 700 : 'inherit')};
    color: ${({ theme, $status }) =>
        $status === 'missing' ? theme.errorColor : $status === 'pending' ? theme.infoTextColor : 'inherit'};
`;

const Meta = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const DetailsLink = styled(Link)`
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.infoTextColor};

    &:hover {
        color: ${({ theme }) => theme.ctaPrimaryColor};
    }
`;
